import { Pipe, PipeTransform } from '@angular/core';
import { isNil, isNumber } from 'lodash-es';

@Pipe({
  standalone: true,
  name: 'fileSize',
})
export class FileSizePipe implements PipeTransform {
  private readonly siUnits: Array<string> = [
    'kB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB',
  ];

  private readonly iceUnits: Array<string> = [
    'KiB',
    'MiB',
    'GiB',
    'TiB',
    'PiB',
    'EiB',
    'ZiB',
    'YiB',
  ];

  transform(
    byteSize: number,
    params?: { si: boolean; decimals: number },
  ): string {
    let size = byteSize;

    if (isNil(size) || !isNumber(size)) {
      size = 0;
    }

    return this.humanFileSize(size, params?.si, params?.decimals);
  }

  /**
   * Format bytes as human-readable text.
   *
   * @param bytes Number of bytes.
   * @param useSi True to use metric (SI) units, aka powers of 1000. False to use
   *           binary (IEC), aka powers of 1024.
   * @param decimalPlaces Number of decimal places to display. (Will still remove insignificant trailing 0)
   *
   * @return Formatted string.
   */
  private humanFileSize(bytes: number, useSi = true, decimalPlaces = 1) {
    const threshold = useSi ? 1000 : 1024;
    const units = useSi ? this.siUnits : this.iceUnits;

    if (Math.abs(bytes) < threshold) {
      return `${bytes} B`;
    }

    let unitIndex = -1;
    const r = 10 ** decimalPlaces;

    do {
      bytes /= threshold;
      ++unitIndex;
    } while (
      Math.round(Math.abs(bytes) * r) / r >= threshold &&
      unitIndex < units.length - 1
    );

    return `${parseFloat(bytes.toFixed(decimalPlaces))} ${units[unitIndex]}`;
  }
}
